import { useState } from "react";

// Extracts the real error message from a supabase.functions.invoke() result —
// same helper used by useTeamData.js for the same reason: supabase-js puts a
// generic string in error.message and the actual JSON body in error.context.
const fnErrMsg = async (error, data) => {
  if (data?.error) return data.error;
  if (!error) return null;
  try {
    const body = await error.context?.json?.();
    if (body?.error) return body.error;
  } catch {}
  return error.message;
};

export function useLeasesData(supabase, reload) {
  const [leases, setLeases] = useState([]);
  const [showLeaseWizard, setShowLeaseWizard] = useState(false);
  const [leaseActionPending, setLeaseActionPending] = useState(false);
  const [leaseActionError, setLeaseActionError] = useState(null);

  // action: "draft" (save only) | "send" (save + email tenant a sign link)
  const submitLease = async ({ action, leaseText, terms, property, tenant, tenantEmail }) => {
    setLeaseActionPending(true);
    setLeaseActionError(null);
    const { data, error } = await supabase.functions.invoke("create-lease", {
      body: { action, leaseText, terms, property, tenant, tenantEmail },
    });
    const errMsg = await fnErrMsg(error, data);
    setLeaseActionPending(false);
    if (errMsg) {
      setLeaseActionError(errMsg);
      return { success: false, error: errMsg };
    }
    reload();
    return { success: true, lease: data.lease };
  };

  // The lease-documents bucket is service-role-only, so fetching a viewable
  // PDF link has to go through this function rather than supabase.storage directly.
  const getLeasePdfUrl = async (leaseId) => {
    const { data, error } = await supabase.functions.invoke("get-lease-pdf-url", {
      body: { leaseId },
    });
    const errMsg = await fnErrMsg(error, data);
    if (errMsg) return { success: false, error: errMsg };
    return { success: true, url: data.url };
  };

  // Read-only pre-check the wizard runs at Step 3, before investing time in
  // clauses/preview. create-lease re-runs the same check at final submission.
  const checkLeaseConflict = async ({ property, tenant, terms }) => {
    const { data, error } = await supabase.functions.invoke("check-lease-conflict", {
      body: { property, tenant, terms },
    });
    const errMsg = await fnErrMsg(error, data);
    if (errMsg) return { hasConflict: true, message: errMsg };
    return { hasConflict: false };
  };

  return {
    leases, setLeases,
    showLeaseWizard, setShowLeaseWizard,
    leaseActionPending, leaseActionError, setLeaseActionError,
    submitLease, getLeasePdfUrl, checkLeaseConflict,
  };
}
